import { FC } from 'react';
import { logger } from '@belong/logging';
import { isString } from '@belong/type-utils';
import { ICatalogOffer, ICatalogOfferReward, IDictionary } from '@belong/types';
import {
  getIdFromOfferOrReward,
  getRewardType,
  isOffer,
  OFFER_TEMPLATE_TYPE,
  offerToDictionary,
  useOfferDetails
} from '@belong/offers';
import { IAppliedOfferReward } from '@belong/types/api/offers';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { OfferAlertWithTemplate } from './OfferAlertWithTemplate';

interface IOfferAlertContainer {
  templateType: OFFER_TEMPLATE_TYPE;
  offerOrReward: string | ICatalogOffer | IAppliedOfferReward;
  dictionary?: IDictionary;
}

// Used to display 1-n Reward alerts
// Optionally loads offer details if offerOrReward is string
//  - For Pre-Activation offers, pass offerOrReward as a string (id)
//  - For Applied offers/promos, pass the reward details
export const OfferAlertContainer: FC<IOfferAlertContainer> = ({ offerOrReward, templateType, dictionary }) => {
  const shouldLoadOfferDetails = isString(offerOrReward);
  const { isLoading, error, offer } = useOfferDetails((offerOrReward ?? '').toString(), shouldLoadOfferDetails);

  // No param:
  if (!offerOrReward) {
    return null;
  }

  const offerId = getIdFromOfferOrReward(offerOrReward);

  // Waiting for Offer Details:
  if (isLoading) {
    return <LoadingState offerId={offerId} />;
  }

  // Load Required and Complete, but failed to load Offer Details:
  if (shouldLoadOfferDetails && (error || !offer)) {
    logger.error(`Failed to load product details for Offer: ${offerId} - error:`, error);
    return <ErrorState offerId={offerId} />;
  }

  let rewards: (ICatalogOfferReward | IAppliedOfferReward)[];
  let offerDictionary: IDictionary = {};

  if (isString(offerOrReward)) {
    rewards = offer?.rewards ?? [];
    if (offer) {
      offerDictionary = offerToDictionary(offer);
    }
  } else if (isOffer(offerOrReward)) {
    rewards = offerOrReward.rewards;

    // ensure final dictionary contains start date and offer name
    offerDictionary = offerToDictionary(offerOrReward);
  } else {
    // Applied offer
    rewards = [offerOrReward];
  }

  // Loaded state:
  const commonProps = {
    offerId,
    templateType,
    dictionary: { ...dictionary, ...offerDictionary }
  };

  return (
    <>
      {rewards.map(reward => {
        const idx = `${offerId}-${getRewardType(reward)}`;
        return <OfferAlertWithTemplate key={`${offerId}-${idx}`} reward={reward} {...commonProps} />;
      })}
    </>
  );
};

OfferAlertContainer.displayName = 'OfferAlertContainer';
